<template>
  <div v-show="isScrollable" class="BackToTop">
    <p v-if="text" class="BackToTop-text">
      {{ text }}
    </p>

    <ActionButton
      :class=" ['BackToTop-button', { 'is-visible': isButtonVisible } ]"
      variant="elevated"
      leftIcon="arrow-upward"
      type="button"
      :aria-label="label"
      @click="scrollToTop"
    >
      {{ label }}
    </ActionButton>
  </div>
</template>

<script setup lang="ts">
import { useScroll } from '@vueuse/core';
import { usePageScrollable } from '@cvo/ui/composables';
import { watch, ref } from 'vue';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';

/**
 * Sits at the end of a scrollable page: optional end of results copy plus a control that
 * scrolls back to the top. Renders nothing while the page fits in the viewport.
 */
export interface BackToTopProps {
  /** Button label. Also the accessible name while the label is hidden on mobile. */
  label: string;
  /** End of results copy shown next to the button. */
  text?: string;
}

defineProps<BackToTopProps>();

const { directions, y: yPosition } = useScroll(window);
const { isScrollable } = usePageScrollable();

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

const isButtonVisible = ref(false);

watch(directions, (newDirections) => {
  if(yPosition.value === 0) {
    isButtonVisible.value = false;
    return;
  }

  if (newDirections.top) {
    isButtonVisible.value = true;
  } else if (newDirections.bottom) {
    isButtonVisible.value = false;
  }
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .BackToTop {
    --back-to-top-bottom-offset: var(--page-padding-y);

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: vars.$dimensions-d-3;

    @include responsive.mobile {
      flex-direction: row;
      align-items: flex-start;
      justify-content: flex-end;
      gap: vars.$dimensions-d-14;
    }

    & &-button {
      @include responsive.mobile {
        position: fixed;
        inset-inline-end: var(--page-padding-x);
        bottom: var(--back-to-top-bottom-offset);
        z-index: calc(vars.$z-index-footer + 1);
        transform: translateY(calc(var(--back-to-top-bottom-offset) + 100px));
        transition: transform vars.$transition-base ease-in-out;

        &.is-visible {
          transform: translateY(0);
        }

        .Button-label {
          display: none;
        }
      }
    }

    &-text {
      color: vars.$organisms-scrollendbutton-text-color;
      margin-inline-end: auto;
      @include text.text('md', vars.$typography-weight-strong);
    }
  }
}
</style>
